<template>
    <RedesignBaseCounterCard
        :widget="data"
        :card-text="cardText"
        :card-value="cardValue"
        :show-text="showTextValue"
        :display-border="displayBorderValue"
        :selected-type="voicebotType"
        :icon="voicebotTypeData.icon"
        :icon-color="voicebotTypeData.color"
        :limit-status-type="notificationStatus"
        animation
        class="voicebot-counter-card"
    >
        <template
            v-if="botsWithActiveCalls.length"
            #content-popover
        >
            <ul class="p-2">
                <li
                    v-for="(bot, index) in botsWithActiveCalls"
                    :key="`${bot.VoicebotID}_${data.WidgetID}`"
                    :class="{
                        'mb-2': index !== botsWithActiveCalls.length - 1
                    }"
                >
                    {{ bot.VoicebotName }}
                    <hr v-if="index !== botsWithActiveCalls.length - 1">
                </li>
            </ul>
        </template>
    </RedesignBaseCounterCard>
</template>

<script setup lang="ts">
/* Widget Voicebot Counter Card */
import RedesignBaseCounterCard from '@/components/RedesignComponents/Cards/RedesignBaseCounterCard.vue'
import { WidgetExtended } from '@/types/dashboard'
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import {
    VOICEBOT_ACTIVE_CALLS_CONFIG,
    VOICEBOT_AVERAGE_CALL_TIME_CONFIG,
    VOICEBOT_MAX_CALL_TIME_CONFIG,
    isVoicebotCounter,
    voicebotTypes
} from '@/enum/voicebotCounters'
import { isObject } from '@/util/objectUtil'
import { useI18n } from 'vue-i18n'
import { useVoiceBotsState } from '@/composables/useVoiceBots'
import useEntityPositiveNegativeFilters from '@/composables/useEntityPositiveNegativeFilters'
import { VOICEBOT_LIST_PARAMETR_NAME } from '@/enum/widgetTemplateConfigs'
import { timeFormatter } from '@/helpers/timeFormatter'
import {
    removeSoundsFromQueueByIdWithHistoryKeep,
    updateSoundCurrentValue,
    updateTimerStateValue
} from '@/composables/useSoundsStoreData'
import { useSoundsThresholdNotification, warningThresholdValue, limitThresholdValue } from '@/composables/useSoundsThresholdNotification'

const notificationCallersProperty = 'max calls'
const notificationProperty = 'max time'
const notificationTag = 'widget.soundBaseCardHtmlRedesign'

const props = withDefaults(
    defineProps<{
        data: WidgetExtended
        editable?: boolean
        onEditMode?: boolean
        showText?: boolean
        displayBorder?: boolean
    }>(),
    {
        showText: true,
        displayBorder: true
    })

const { t } = useI18n()

/* Computed voicebotType from widget layout */
const voicebotType = computed(() => {
    return props.data.WidgetLayout?.voicebotType ?? 0
})

const showTextValue = computed(() => {
    return props.data.WidgetLayout?.showText ?? props.showText
})

const displayBorderValue = computed(() => {
    return props.data.WidgetLayout?.displayBorder ?? props.displayBorder
})

const notificationStatus = ref('')
let intervalId: ReturnType<typeof setInterval> | null = null
const tick = ref(Date.now())

/* Composable */
const { allBotsWithCalls } = useVoiceBotsState()

/* Computed */
const widgetData = computed(() => {
    return props.data
})

const allBotsWithLiveCalls = computed(() => {
    return allBotsWithCalls.value.map(bot => {
        return {
            ...bot,
            Calls: bot.Calls.filter(call => call.callStatus !== 'Ended')
        }
    })
})

const { filteredItems: filteredBots } = useEntityPositiveNegativeFilters(
    widgetData,
    VOICEBOT_LIST_PARAMETR_NAME,
    allBotsWithLiveCalls,
    'VoicebotID'
)

const voicebotTypeData = computed(() => {
    return voicebotTypes[voicebotType.value] || VOICEBOT_ACTIVE_CALLS_CONFIG
})

const cardValue = computed(() => {
    switch (voicebotType.value) {
        case VOICEBOT_ACTIVE_CALLS_CONFIG.id:
            return getVoicebotStats().totalActiveCalls
        case VOICEBOT_AVERAGE_CALL_TIME_CONFIG.id:
            return formatTime(getVoicebotStats().averageCallDuration)
        case VOICEBOT_MAX_CALL_TIME_CONFIG.id:
            return formatTime(getVoicebotStats().maxCallDuration)
        default:
            return getVoicebotStats().totalActiveCalls
    }
})

const defaultCardText = computed(() => {
    return t(voicebotTypeData.value.text)
})

const cardText = computed(() => {
    return props.data.WidgetLayout?.ContentTitle &&
        isObject(props.data.WidgetLayout.ContentTitle) &&
        (props.data.WidgetLayout.ContentTitle[voicebotType.value] ||
            props.data.WidgetLayout.ContentTitle[voicebotType.value] === '')
        ? props.data.WidgetLayout.ContentTitle[voicebotType.value]
        : defaultCardText.value
})

const botsWithActiveCalls = computed(() => {
    return filteredBots.value.filter(bot => bot.Calls && bot.Calls.length > 0)
})

const {
    getThresholdValue,
    getCurrentNotificationStatus,
    setupNotification: setupSoundNotification
} = useSoundsThresholdNotification(widgetData.value)

/* Methods */
function getVoicebotStats () {
    let totalActiveCalls = 0
    let totalDuration = 0
    let maxDuration = 0
    const currentTime = tick.value

    filteredBots.value.forEach((bot) => {
        const calls = bot.Calls || []
        calls.forEach((call) => {
            totalActiveCalls++
            const callStartTime = call.callStarted_UTC_CLIENT || call.callStarted
            if (callStartTime) {
                const duration = Math.max(0, currentTime - callStartTime)
                totalDuration += duration
                if (duration > maxDuration) {
                    maxDuration = duration
                }
            }
        })
    })

    const averageCallDuration = totalActiveCalls > 0
        ? Math.floor(totalDuration / totalActiveCalls / 1000)
        : 0

    return {
        totalActiveCalls,
        averageCallDuration,
        maxCallDuration: Math.floor(maxDuration / 1000)
    }
}

function formatTime (seconds: number): string {
    return timeFormatter(seconds) || '00:00'
}

function setCounterState () {
    const stats = getVoicebotStats()

    if (isVoicebotCounter(voicebotType.value)) {
        handleCallsNotification(stats.totalActiveCalls)
        return
    }

    // For time-based counters, check threshold against duration
    const durationValue = voicebotType.value === VOICEBOT_MAX_CALL_TIME_CONFIG.id
        ? stats.maxCallDuration
        : stats.averageCallDuration

    handleTimeNotification(durationValue)
}

const updateNotification = (val: number, type: string, id: string) => {
    const notificationData = {
        notification_value: getThresholdValue(type),
        entity_name: cardText.value,
        current_value: `${cardValue.value}.`,
        property: notificationCallersProperty,
        type: type
    }
    const notificationHtml = t(notificationTag, { ...notificationData })
    updateSoundCurrentValue(id, notificationHtml)
}

const handleCallsNotification = (val: number) => {
    const currentNotificationStatus = getCurrentNotificationStatus(val)

    if (!currentNotificationStatus && !notificationStatus.value) return
    if (!currentNotificationStatus) {
        notificationStatus.value = ''
        removeSoundsFromQueueByIdWithHistoryKeep(`${props.data.WidgetID}`)
        return
    }
    if (currentNotificationStatus === notificationStatus.value) {
        updateNotification(val, currentNotificationStatus, `${props.data.WidgetID}`)
        return
    }
    removeSoundsFromQueueByIdWithHistoryKeep(`${props.data.WidgetID}`)
    const shouldSoundNotBePlayed = notificationStatus.value === limitThresholdValue &&
        currentNotificationStatus === warningThresholdValue
    notificationStatus.value = currentNotificationStatus

    setupSoundNotification({
        entityName: cardText.value,
        property: notificationCallersProperty,
        soundPlayed: shouldSoundNotBePlayed,
        currentValue: `${cardValue.value}.`,
        id: `${props.data.WidgetID}`,
        notificationTag,
        type: currentNotificationStatus,
    })
}

const handleTimeNotification = (val: number) => {
    const currentNotificationStatus = getCurrentNotificationStatus(val)

    if (!currentNotificationStatus && !notificationStatus.value) return

    if (!currentNotificationStatus) {
        notificationStatus.value = ''
        removeSoundsFromQueueByIdWithHistoryKeep(`${props.data.WidgetID}`)
        return
    }

    if (currentNotificationStatus === notificationStatus.value) {
        updateTimerStateValue(`${props.data.WidgetID}`, val)
        return
    }
    removeSoundsFromQueueByIdWithHistoryKeep(`${props.data.WidgetID}`)
    const shouldSoundNotBePlayed = notificationStatus.value === limitThresholdValue &&
        currentNotificationStatus === warningThresholdValue

    notificationStatus.value = currentNotificationStatus

    setupSoundNotification({
        entityName: cardText.value,
        property: notificationProperty,
        soundPlayed: shouldSoundNotBePlayed,
        currentValue: '',
        id: `${props.data.WidgetID}`,
        notificationTag,
        type: currentNotificationStatus,
        timerStartValue: val
    })
}

/* Lifecycle */
onMounted(() => {
    setCounterState()
    intervalId = setInterval(() => {
        tick.value = Date.now()
        setCounterState()
    }, 1000)
})

onBeforeUnmount(() => {
    if (intervalId) {
        clearInterval(intervalId)
        intervalId = null
    }
})

/* Watchers */
watch(
    voicebotType,
    () => {
        setCounterState()
    },
    { immediate: true }
)

watch(
    filteredBots,
    () => {
        setCounterState()
    },
    { deep: true }
)
</script>

